import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {EventEmitter, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {CommandService} from '@core/services/command.service';
import {DialogService} from '@shared/services/dialog-service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {PlatformCommand} from '@shared/commands/command-base';
import {FileManagementService} from '@core/services/file-management.service';
import {RootDynamicComponentHelper} from '@shared/helpers/root-dynamic-component-helper';
import {
  SetActivityFeedAction,
  SetCurrentRightSideComponentAction
} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {Store} from '@ngrx/store';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import {PlatformSidebarService} from '@shared/components/platform-sidebar/store/platform-sidebar.service';
import {FuseSidebarService} from '@barebone-fuse/@fuse/components/sidebar/sidebar.service';
import {Api} from "@shared/constants/common-field.constants";

@Injectable({
  providedIn: 'root',
})
export class AuditTrackerHandler implements IPlatformToolHandler {
  magicLinkModelValueChanged: EventEmitter<any> = new EventEmitter();
  magicLinkModel: MagicLinkModel;
  selectedTabModel: TabModel;
  ngUnsubscribe = new Subject<void>();
  invalidateSubject: InvalidateSubject;

  constructor(private commandService: CommandService,
              private router: Router,
              private dialogService: DialogService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private snackBarService: SnackBarService,
              private fileManagementService: FileManagementService,
              private platformSidebarStore: Store<PlatformSidebarState>,
              private platformSidebarService: PlatformSidebarService,
              private sidebarService: FuseSidebarService,
  ) {
  }

  executeCommand(command: PlatformCommand) {
    console.log('[audit-tracker-platform-command] onPlatformTool Handler Command', command);
    if (command.fieldValues['CommandHandler']) {
      const commandHandler = RootDynamicComponentHelper.getComponent(command.fieldValues['CommandHandler']);
      if (commandHandler) {
        commandHandler.executeCommand(command, this, null);
        return;
      }
    }
    switch (command.name) {
      case 'ShowArchivedCommand':
        // Handle Show Archived
        console.log('Show archived command clicked');
        this.handleShowArchivedCommand(command);
        break;
      default:
        this.executeCommandToApi(command);
        break;
    }
  }

  valueChanged($event) {
    console.log('Inside Handler', $event);
    if (this.handleAction($event)) {
      return;
    }
    if (this.handleCommand($event)) {
      return;
    }
    if (this.handleByFieldDefinition($event)) {
      return;
    }
  }

  destroy() {
    if (this.sidebarService.getSidebar('rightsidebar')) {
      this.sidebarService.getSidebar('rightsidebar').close();
      this.sidebarService.getSidebar('rightsidebar').fold();
    }
    this.platformSidebarService.hideSidebarIcon('Audit Tracker');
    this.invalidateSubject.close();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setSelectedTab(selectedTab: TabModel): void {
  }

  private executeCommandToApi(command: PlatformCommand) {
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();
      command.projectId = '[PROJECT_ID]';
      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
          console.log('[audit-tracker-platform-command] Command Response', commandResponse);
          this.fuseSplashScreenService.hide();
          if (commandResponse.fieldValues['RedirectUrl']) {
            this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
          }
          if (commandResponse.fieldValues['RedirectCommand']) {
            var redirectCommand = commandResponse.fieldValues['RedirectCommand'];
            this.executeCommand(redirectCommand);
          }
          if (commandResponse.fieldValues['SnackBarMessage']) {
            const duration = commandResponse.fieldValues['SnackBarMessage'].length * 80;
            this.snackBarService.showInformation(commandResponse.fieldValues['SnackBarMessage'], duration);
          }
          if (commandResponse.fieldValues['ErrorMessage']) {
            this.snackBarService.showError(commandResponse.fieldValues['SnackBarMessage']);
          }

          if (commandResponse.fieldValues['FileId']) {
            const fileId = commandResponse.fieldValues['FileId'];
            const fileName = commandResponse.fieldValues['FileName'];

            this.downloadFile(fileId, fileName);
          }

          this.invalidateSubject.invalidate();
        },
        error => {
          console.log(error);
          this.fuseSplashScreenService.hide();
        });
    }
  }

  private handleCommand($event): boolean {
    if ($event.command) {
      switch ($event.command.name) {
        case 'CommandName':
          return true;
        default:
          this.executeCommand($event.command);
          return true;
      }
    }
    return false;
  }

  private handleAction($event): boolean {
    if ($event.action) {
      console.log('Action ID: ' + $event.action.id);
      const item = $event.item;
      const command = this.magicLinkModel.additionalCommands[$event.action.id];
      if (command) {
        command.fieldValues['AuditTrackerId'] = item.id;
        command.fieldValues['IsClosed'] = this.isClosed(item);
        this.executeCommand(command);
        return true;
      }
      switch ($event.action.id) {
        case 'Documentation':
          this.navigateToDocumentsRepository(item);
          return true;
        case 'ViewActivities':
          this.viewActivities(item);
          return true;
        case 'Edit':
          this.edit($event);
          return true;
        case "ManagePermissions":
          this.managePermissions($event);
          return true;
      }
    }
    return false;
  }

  private edit($event) {
    this.openEditor($event);
  }

  private openEditor($event) {
    const originalValue = $event.item.cells[$event.fieldDefinition.key]?.value;
    this.dialogService.showCellEditorDialog($event.item, $event.fieldDefinition).pipe(takeUntil(this.ngUnsubscribe)).subscribe(state => {
      console.log('After close state from audit-tracker handler: ', state);
      if (state && state.action === 'OK') {
        const item = {id: $event.item.id, fieldValues: {}};
        const value = $event.item.cells[$event.fieldDefinition.key].value;
        item.fieldValues[$event.fieldDefinition.key] = value;
        const command = new PlatformCommand('UpdateAuditTrackerCommand', 'AuditTrackerApi');
        command.fieldValues['AuditTrackerId'] = $event.item.id;
        command.fieldValues['FieldKey'] = $event.fieldDefinition.key;
        command.fieldValues[$event.fieldDefinition.key] = value;
        command.fieldValues['Value'] = value;
        command.fieldValues['FieldHeader'] = $event.fieldDefinition.header;
        this.executeCommand(command);
      } else {
        // change back to original value
        $event.item.cells[$event.fieldDefinition.key].value = originalValue;
      }
    });
  }

  private isClosed(item: any): boolean {
    return item.cells['IsClosed'] && item.cells['IsClosed'].value;
  }

  private handleByFieldDefinition($event): boolean {
    const command = this.magicLinkModel.additionalCommands[$event.fieldDefinition.key];
    if (command) {
      command.fieldValues['AuditTrackerId'] = $event.item.id;
      if ($event.item.cells['FlowExecutionId'] && $event.item.cells['FlowExecutionId'].value) {
        command.fieldValues['FlowExecutionId'] = $event.item.cells['FlowExecutionId'].value;
      }
      this.executeCommand(command);
      return true;
    }
    return false;
    // switch ($event.fieldDefinition.key) {
    //   case 'CaseId':
    //   case 'FlowExecutionId':
    //     const item = $event.item;
    //     if (item.cells['FlowExecutionId'] && item.cells['FlowExecutionId'].value) {
    //       this.navigateToWorkflow(item);
    //     } else {
    //       this.showStartWorkFlowDialog($event.item);
    //     }
    //     return true;
    // }
    // return false;
  }

  private navigateToDocumentsRepository(item: any) {
    if (item.cells['DocumentsDirectoryId'] && item.cells['DocumentsDirectoryId'].value) {
      const command = new PlatformCommand('OpenFolderCommand', 'FileStoreApi');
      command.fieldValues['IsClientDrive'] = true;
      command.fieldValues['ItemId'] = item.cells['DocumentsDirectoryId'].value;
      this.executeCommand(command);
    }
  }

  private downloadFile(fileId: string, fileName: string) {
    this.fileManagementService.downloadClientFileWithId(fileId).subscribe(payload => {
      this.fileManagementService.openSaveFileWindow(payload, fileName);
      this.fuseSplashScreenService.hide();
    });
  }

  private handleShowArchivedCommand(command: PlatformCommand) {
    const updateViewCommand = new PlatformCommand('CreateAuditTrackerLinkCommand', 'AuditTrackerApi');
    updateViewCommand.fieldValues['ShowArchived'] = command.isChecked;
    updateViewCommand.projectId = command.projectId;
    this.fuseSplashScreenService.show();
    this.commandService.executePlatformCommand(updateViewCommand).pipe(take(1)).subscribe(response => {
        console.log('commandState', response);
        if (response.fieldValues['PlatformLinkModel']) {

          this.magicLinkModel = response.fieldValues['PlatformLinkModel'];
          // this.selectedTabModel = this.magicLinkModel.tabBar.tabs[this.magicLinkModel.tabBar.selectedIndex];

          this.magicLinkModelValueChangedHandler({
            id: 'PlatformToolContainerComponent', value: response.fieldValues['PlatformLinkModel'],
            magicLinkModel: this.magicLinkModel
          });

          // this.magicLinkModelValueChangedHandler({id: 'PlatformToolVirtualGridComponent',
          // value: response.fieldValues['PlatformLinkModel'],
          //   magicLinkModel: this.magicLinkModel});

          // this.invalidateSubject.invalidate();
        } else {
          console.log('Platform link model not found');
          this.fuseSplashScreenService.hide();
        }
      },
      error => {
        this.fuseSplashScreenService.hide();
        console.error('Operation failed!!!', error);
      });
  }

  private magicLinkModelValueChangedHandler(command: any) {
    this.magicLinkModelValueChanged.emit(command);
  }

  private viewActivities(item: any) {
    const referenceCode = item.id;
    const title = item.cells['Title'].value;
    const activityInfo = {type: 'ReferenceCode', 'id': referenceCode, 'title': title, isDefault: false, isUserAction: true};
    this.platformSidebarStore.dispatch(new SetActivityFeedAction(activityInfo));
  }

  private managePermissions($event: any) {
    const managePermissionCommand = new PlatformCommand('ManagePermissionDialogCommand', Api.Security);
    managePermissionCommand.fieldValues['DataPointId'] = $event.item.id;
    managePermissionCommand.fieldValues['FeatureId'] = 'ADT';
    this.executeCommand(managePermissionCommand);
  }
}
